<script setup lang="ts">
import type { Property } from '~/core/entities/property';

import { cn } from '@/utils';
import { breakpointsTailwind } from '@vueuse/core';
import { usePropertyPhotoCounter } from '~/composables/usePropertyPhotoCounter';

import { PropertyHeroActionButtons } from '~/components/PropertyHeroActionButtons';
import { PropertyHeroCarousels } from '~/components/PropertyHeroCarousels';
import { PropertyHeroNavigationControls } from '~/components/PropertyHeroNavigationControls';
import { PropertyHeroShareControls } from '~/components/PropertyHeroShareControls';

interface PropertyHeroSectionProps {
	property: Property | null;
	isLoading: boolean;
	launchYear: string;
	shouldShowSimilarPropertyCta: boolean;
}

interface PropertyHeroSectionEmits {
	'photo-modal-opened': [
		sectionName: import('~/types/mixpanel').SectionName,
		clickType: 'photo' | 'cta',
		photoCount: number,
		photoUrl?: string,
		ctaText?: string
	];
	'video-modal-opened': [clickType: 'video' | 'cta', videoUrl?: string];
	'map-modal-opened': [
		sectionName: import('~/types/mixpanel').SectionName,
		ctaText: string
	];
	'form-property-clicked': [
		params: {
			sectionName: string;
			ctaText: string;
			companyName: string;
			agentName: string;
			askingPrice: number | null;
			openModal: boolean;
		}
	];
	'similar-clicked': [];
	'photo-clicked': [photoIndex: number];
}

const props = defineProps<PropertyHeroSectionProps>();
const emit = defineEmits<PropertyHeroSectionEmits>();

const { ft_poc_videos } = useFeatureFlags();

const propertyRef = computed(() => props.property);

const {
	numberOfImages,
	numberOfVideos,
	desktopPhotoModalCtaText,
	mobilePhotoModalCtaText
} = usePropertyPhotoCounter({
	property: propertyRef
});

const isNotDesktop = useBreakpoints(breakpointsTailwind).smaller('xl');

const sectionClass = computed(() =>
	cn(
		'relative flex w-full overflow-hidden bg-primary md:h-[440px] xl:h-[480px] 3xl:h-[520px]',
		ft_poc_videos ? 'h-[68dvh] min-h-[540px]' : 'h-[30dvh] min-h-[300px]'
	)
);

function handlePhotoClicked() {
	const currentCtaText = isNotDesktop.value
		? mobilePhotoModalCtaText.value
		: desktopPhotoModalCtaText.value;

	emit(
		'photo-modal-opened',
		'hero section',
		'cta',
		0,
		undefined,
		currentCtaText
	);
}

function handleVideoClicked() {
	emit('video-modal-opened', 'cta');
}

function handleMapClicked() {
	emit('map-modal-opened', 'hero section', 'Mapa');
}

function handleCarouselPhotoClicked(payload: {
	photoIndex: number;
	photoUrl: string;
	clickType: 'photo' | 'cta';
}) {
	emit('photo-clicked', payload.photoIndex);
}

function handleSimilarClicked() {
	emit('similar-clicked');
}
</script>

<template>
	<section :class="sectionClass">
		<div class="relative flex size-full flex-wrap overflow-hidden bg-white">
			<PropertyHeroNavigationControls :property="property" />

			<PropertyHeroActionButtons
				:property="property"
				:number-of-images="numberOfImages"
				:number-of-videos="numberOfVideos"
				:should-show-similar-property-cta="shouldShowSimilarPropertyCta"
				@photo-clicked="handlePhotoClicked"
				@video-clicked="handleVideoClicked"
				@map-clicked="handleMapClicked"
				@similar-clicked="handleSimilarClicked"
			>
				<template #photo-modal-cta-text>
					<!-- Using visibility instead of display to prevent CLS -->
					<span class="invisible absolute xl:visible xl:relative">{{
						desktopPhotoModalCtaText
					}}</span>
					<span class="visible relative xl:invisible xl:absolute">{{
						mobilePhotoModalCtaText
					}}</span>
				</template>
			</PropertyHeroActionButtons>

			<PropertyHeroCarousels
				ref="carouselsRef"
				:property="property"
				:is-loading="isLoading"
				@photo-clicked="handleCarouselPhotoClicked"
			/>

			<PropertyHeroShareControls :property="property" />
		</div>
	</section>
</template>
